import type { LinkProps } from '@tanstack/react-router'
import { queryOptions, useMutation, useQueryClient } from '@tanstack/react-query'

import type { Language } from '@/bindings/Language'
import type { Notification } from '@/bindings/Notification'
import type { NotificationListResponse } from '@/bindings/NotificationListResponse'
import type { UnreadCount } from '@/bindings/UnreadCount'
import { PURPOSE_LABELS } from '@/features/otp-requests/api'
import { request } from '@/lib/api'
import type { System } from '@/lib/auth-store'
import type { Translate } from '@/lib/i18n/translate'
import { PAGE_SIZE } from '@/lib/paging'
import { profilePathFor } from '@/lib/return-path'
import { ROLE_LABELS, isRole } from '@/lib/roles'
import { formatDate } from '@/lib/time'

/** The bell's poll: a minute is plenty for notices that are never urgent. */
export const UNREAD_POLL_MS = 60_000

/** How many notices the bell's panel shows; the page has the rest. */
export const RECENT_COUNT = 5

function apiPath(system: System, suffix = '') {
  return system === 'platform' ? `/platform/notifications${suffix}` : `/notifications${suffix}`
}

/** `all` prefixes the rest, so one invalidation after a mark-read refreshes the bell, the panel and the list. */
export const notificationKeys = {
  all: (system: System) => ['notifications', system] as const,
  unread: (system: System) => ['notifications', system, 'unread'] as const,
  list: (system: System, page: number, perPage: number) => ['notifications', system, 'list', page, perPage] as const,
}

export function unreadCountQuery(system: System) {
  return queryOptions({
    queryKey: notificationKeys.unread(system),
    queryFn: () => request<UnreadCount>(apiPath(system, '/unread')),
    refetchInterval: UNREAD_POLL_MS,
  })
}

/** The page's list; the bell's panel asks for the newest `RECENT_COUNT` of page 1. */
export function notificationListQuery(system: System, page: number, perPage = PAGE_SIZE) {
  return queryOptions({
    queryKey: notificationKeys.list(system, page, perPage),
    queryFn: () => request<NotificationListResponse>(`${apiPath(system)}?page=${page}&per_page=${perPage}`),
  })
}

/** After a mark-read every view refreshes: the bell, the panel and the list. */
function useRefresh(system: System) {
  const queryClient = useQueryClient()
  return () => queryClient.invalidateQueries({ queryKey: notificationKeys.all(system) })
}

/**
 * A notice is read when the user clicks it or presses "Mark all read" —
 * nothing else touches it (2026-09-13).
 */
export function useReadAll(system: System) {
  const refresh = useRefresh(system)
  return useMutation({
    mutationFn: () => request<void>(apiPath(system, '/read-all'), { method: 'POST' }),
    onSuccess: refresh,
  })
}

export function useReadOne(system: System) {
  const refresh = useRefresh(system)
  return useMutation({
    mutationFn: (id: string) => request<void>(apiPath(system, `/${id}/read`), { method: 'POST' }),
    onSuccess: refresh,
  })
}

export interface Rendered {
  text: string
  /** Where the notice leads, when it has somewhere to go. */
  link?: LinkProps
}

const str = (value: unknown) => (typeof value === 'string' ? value : '')

/**
 * Key + params → localized text (T4.8). The keys are the producers':
 * `identifier_claimed` (C6), `otp_requested` (E5), `role_granted` /
 * `role_revoked` (E2/E7). `feedback.received` (I3), `restaurant.created` (K1), `notice.*` (K3),
 * `restaurant.removed` (K4, unlinked: the restaurant is gone).
 * An unknown key still shows, by name, so a new producer is never silent.
 */
export function renderNotification(system: System, { key, params }: Notification, t: Translate, language: Language): Rendered {
  const date = (value: unknown) => (typeof value === 'string' && value ? formatDate(value, language) : '')
  switch (key) {
    case 'identifier_claimed': {
      const channel = params.channel === 'email' ? t('auth.email') : t('auth.phone')
      return {
        text: t('notification.identifier_claimed', { channel: channel.toLowerCase(), identifier: str(params.identifier) }),
        link: { to: profilePathFor(system) },
      }
    }
    case 'otp_requested': {
      const purpose = params.purpose === 'reset_password' ? 'reset_password' : 'verify_phone'
      return {
        text: t('notification.otp_requested', { purpose: t(PURPOSE_LABELS[purpose]) }),
        link: { to: '/otp-requests', search: { page: 1, status: 'pending' } },
      }
    }
    case 'role_granted':
    case 'role_revoked': {
      const role = isRole(params.role) ? t(ROLE_LABELS[params.role]) : str(params.role)
      const restaurant = str(params.restaurant_name)
      const restaurantSlug = str(params.restaurant_slug)
      return {
        text: t(`notification.${key}`, { role, restaurant }),
        ...(key === 'role_granted' && restaurantSlug
          ? { link: { to: '/$restaurantSlug/manage', params: { restaurantSlug } } }
          : {}),
      }
    }
    case 'feedback.received': {
      if (system === 'platform') return { text: t('notification.feedback.app'), link: { to: '/feedback', search: { page: 1 } } }
      const restaurant = str(params.restaurant_name)
      const restaurantSlug = str(params.restaurant_slug)
      return {
        text: t('notification.feedback.restaurant', { restaurant }),
        ...(restaurantSlug ? { link: { to: '/$restaurantSlug/manage/feedback', params: { restaurantSlug }, search: { page: 1 } } } : {}),
      }
    }
    case 'restaurant.created': {
      const restaurant = str(params.restaurant_name)
      const restaurantId = str(params.restaurant_id)
      const creator = str(params.creator_name) || t('notification.someone')
      return {
        text: t('notification.restaurant_created', { creator, restaurant }),
        ...(restaurantId ? { link: { to: '/restaurants/$restaurantId', params: { restaurantId }, search: {} } } : {}),
      }
    }
    case 'notice.payment_reminder':
    case 'notice.deactivation_warning':
    case 'notice.deactivated':
    case 'notice.payment_received':
    case 'notice.reactivated':
    case 'notice.removal_notice': {
      // K3: the platform's templated notices to manager+; the lock notice on the manage page is where they lead.
      const restaurant = str(params.restaurant_name)
      const restaurantSlug = str(params.restaurant_slug)
      return {
        text: t(`notification.${key}`, {
          restaurant,
          amount: str(params.amount),
          dueDate: date(params.due_date),
          deactivateOn: date(params.deactivate_on),
          removeOn: date(params.remove_on),
        }),
        ...(restaurantSlug ? { link: { to: '/$restaurantSlug/manage', params: { restaurantSlug } } } : {}),
      }
    }
    case 'restaurant.removed':
      return { text: t('notification.restaurant_removed', { restaurant: str(params.restaurant_name) }) }
    default:
      return { text: t('notification.unknown', { key }) }
  }
}
